import { z } from "zod";
import { RAW_PAX_CREDITS_PER_DISPLAY_CREDIT } from "../../accounting/shared/constants";
import { RawPaxQuantity } from "../../accounting/v1alpha1/schemas";
import { Permission, ProfileSummary, Timestamp, UserId } from "../../common";

const EXAMPLE = {
  roleId: "0199f0c2-1a3b-7c4d-8e5f-6a7b8c9d0e1f",
  assignmentId: "0199f0c2-5e6f-7a8b-9c0d-1e2f3a4b5c6d",
  userId: "0199f0be-4d1a-7b55-8c71-59a0d2e6c884",
  profile: {
    profileId: "0199f0be-4d1a-7b55-8c71-59a0d2e6c884",
    username: "Grace_Hopper",
    displayName: "Grace Hopper",
    avatar: null,
  },
  managerProfile: {
    profileId: "0199f0be-9c07-7a24-b1e8-3f5d8a1c6207",
    username: "Ada_Lovelace",
    displayName: "Ada Lovelace",
    avatar: null,
  },
  pay: "5000000000000",
  defaultPay: "2500000000000",
  timestamp: "2026-09-04T17:00:00.000Z",
  payDate: "2026-10-04",
  lastPaidDate: "2026-09-04",
} as const;

const ASSIGNMENT_EXAMPLE = {
  id: EXAMPLE.assignmentId,
  user: EXAMPLE.profile,
  role: { id: EXAMPLE.roleId, name: "Eval reviewer" },
  startedAt: EXAMPLE.timestamp,
  endedAt: null,
  assignedBy: EXAMPLE.managerProfile,
  endedBy: null,
} as const;

const hasAField = (body: object) => Object.values(body).some(value => value !== undefined);

export const StaffRoleId = z.uuid().brand<"StaffRoleId">().meta({
  id: "StaffRoleId",
  description: "The stable id of a staff role.",
  example: EXAMPLE.roleId,
});
export type StaffRoleId = z.infer<typeof StaffRoleId>;

export const StaffRoleAssignmentId = z.uuid().brand<"StaffRoleAssignmentId">().meta({
  id: "StaffRoleAssignmentId",
  description: "The stable id of one person's assignment to one staff role.",
  example: EXAMPLE.assignmentId,
});
export type StaffRoleAssignmentId = z.infer<typeof StaffRoleAssignmentId>;

export const PayDate = z.iso.date().meta({
  id: "PayDate",
  description: "A calendar date in UTC, written `YYYY-MM-DD`.",
  example: EXAMPLE.payDate,
});
export type PayDate = z.infer<typeof PayDate>;

/** The most one person can be paid in a month. */
export const MAX_STAFF_PAY_CREDITS = 200;

/** `MAX_STAFF_PAY_CREDITS` in raw Pax credits. */
export const MAX_STAFF_PAY_RAW = BigInt(MAX_STAFF_PAY_CREDITS) * RAW_PAX_CREDITS_PER_DISPLAY_CREDIT;

export const MonthlyPay = RawPaxQuantity.refine(raw => BigInt(raw) <= MAX_STAFF_PAY_RAW, {
  error: `Pay can be at most ${MAX_STAFF_PAY_CREDITS} Pax credits a month.`,
  when: payload => payload.issues.length === 0,
}).meta({
  id: "MonthlyPay",
  description: `Raw Pax credits paid once a month, as a base-10 integer string, at most ${MAX_STAFF_PAY_CREDITS} Pax credits.`,
  example: EXAMPLE.pay,
});
export type MonthlyPay = z.infer<typeof MonthlyPay>;

const RoleName = z.string().trim().min(1).max(80).meta({
  description: "The job title shown to staff, unique among active roles regardless of case.",
  example: "Eval reviewer",
});

const RoleDescription = z.string().trim().max(280).nullable().meta({
  description: "What the job involves, or null.",
  example: "Runs evals and reviews their results.",
});

const RolePermissions = z.array(Permission).meta({
  description: "The permissions everyone holding the role receives.",
  example: ["agents.manage"],
});

const RoleDefaultPay = MonthlyPay.nullable().meta({
  description:
    "The monthly pay suggested for someone given the role, or null to suggest none. A person's first paid role starts their pay at it.",
});

export const StaffRole = z
  .strictObject({
    id: StaffRoleId,
    name: RoleName,
    description: RoleDescription,
    permissions: RolePermissions,
    defaultPay: RoleDefaultPay,
    activeAssignmentCount: z.number().int().nonnegative().meta({
      description: "How many people hold the role now.",
      example: 3,
    }),
    archivedAt: Timestamp.nullable().meta({
      description: "When the role was archived, or null while it can be assigned.",
    }),
    createdAt: Timestamp,
    updatedAt: Timestamp,
  })
  .meta({
    id: "StaffRole",
    description: "A staff job and the permissions it grants.",
    example: {
      id: EXAMPLE.roleId,
      name: "Eval reviewer",
      description: "Runs evals and reviews their results.",
      permissions: ["agents.manage"],
      defaultPay: EXAMPLE.defaultPay,
      activeAssignmentCount: 3,
      archivedAt: null,
      createdAt: EXAMPLE.timestamp,
      updatedAt: EXAMPLE.timestamp,
    },
  });
export type StaffRole = z.infer<typeof StaffRole>;

export const StaffRoleSummary = z.strictObject({ id: StaffRoleId, name: RoleName }).meta({
  id: "StaffRoleSummary",
  description: "A staff role's id and name.",
  example: ASSIGNMENT_EXAMPLE.role,
});
export type StaffRoleSummary = z.infer<typeof StaffRoleSummary>;

export const StaffRoleAssignment = z
  .strictObject({
    id: StaffRoleAssignmentId,
    user: ProfileSummary.meta({ description: "The person holding the role." }),
    role: StaffRoleSummary.meta({ description: "The role held." }),
    startedAt: Timestamp,
    endedAt: Timestamp.nullable().meta({
      description: "When the role was unassigned, or null while the person holds it.",
    }),
    assignedBy: ProfileSummary.nullable().meta({
      description: "Who assigned the role, or null when that account no longer exists.",
    }),
    endedBy: ProfileSummary.nullable().meta({
      description:
        "Who unassigned the role, or null while it is held or when that account no longer exists.",
    }),
  })
  .meta({
    id: "StaffRoleAssignment",
    description: "One person holding one staff role.",
    example: ASSIGNMENT_EXAMPLE,
  });
export type StaffRoleAssignment = z.infer<typeof StaffRoleAssignment>;

export const StaffRoleIdParams = z
  .strictObject({
    roleId: StaffRoleId.meta({ description: "The role to act on.", example: EXAMPLE.roleId }),
  })
  .meta({ id: "StaffRoleIdParams", description: "A route parameter naming one staff role." });
export type StaffRoleIdParams = z.infer<typeof StaffRoleIdParams>;

export const StaffRoleAssignmentIdParams = z
  .strictObject({
    assignmentId: StaffRoleAssignmentId.meta({
      description: "The assignment to act on.",
      example: EXAMPLE.assignmentId,
    }),
  })
  .meta({
    id: "StaffRoleAssignmentIdParams",
    description: "A route parameter naming one staff role assignment.",
  });
export type StaffRoleAssignmentIdParams = z.infer<typeof StaffRoleAssignmentIdParams>;

export const ListStaffRolesResponse = z
  .strictObject({
    roles: z.array(StaffRole).meta({ description: "Every role, active and archived, by name." }),
  })
  .meta({ id: "ListStaffRolesResponse", description: "Every staff role." });
export type ListStaffRolesResponse = z.infer<typeof ListStaffRolesResponse>;

export const CreateStaffRoleRequest = z
  .strictObject({
    name: RoleName,
    description: RoleDescription.optional(),
    permissions: RolePermissions,
    defaultPay: RoleDefaultPay,
  })
  .meta({
    id: "CreateStaffRoleRequest",
    description: "A new role. Every permission must be one the caller can grant.",
    example: {
      name: "Eval reviewer",
      permissions: ["agents.manage"],
      defaultPay: EXAMPLE.defaultPay,
    },
  });
export type CreateStaffRoleRequest = z.infer<typeof CreateStaffRoleRequest>;

export const UpdateStaffRoleRequest = CreateStaffRoleRequest.partial()
  .refine(hasAField, { message: "At least one role field is required." })
  .meta({
    id: "UpdateStaffRoleRequest",
    description:
      "The fields to change. A permission change applies to everyone holding the role at once; a default pay change leaves everyone's pay alone.",
    example: { defaultPay: EXAMPLE.defaultPay },
  });
export type UpdateStaffRoleRequest = z.infer<typeof UpdateStaffRoleRequest>;

export const StaffRoleResponse = z
  .strictObject({ role: StaffRole })
  .meta({ id: "StaffRoleResponse", description: "The role after the change." });
export type StaffRoleResponse = z.infer<typeof StaffRoleResponse>;

export const ListStaffRoleAssignmentsQuery = z
  .strictObject({
    userId: UserId.optional().meta({ description: "Only this person's assignments." }),
    roleId: StaffRoleId.optional().meta({ description: "Only this role's assignments." }),
    state: z.enum(["active", "ended"]).default("active").meta({
      description: "`active` for roles held now, `ended` for roles that were unassigned.",
      example: "active",
    }),
  })
  .meta({
    id: "ListStaffRoleAssignmentsQuery",
    description: "Filters for the staff roster.",
  });
export type ListStaffRoleAssignmentsQuery = z.infer<typeof ListStaffRoleAssignmentsQuery>;

export const ListStaffRoleAssignmentsResponse = z
  .strictObject({
    assignments: z.array(StaffRoleAssignment).meta({
      description: "The matching assignments, newest first.",
    }),
  })
  .meta({ id: "ListStaffRoleAssignmentsResponse", description: "The staff roster." });
export type ListStaffRoleAssignmentsResponse = z.infer<typeof ListStaffRoleAssignmentsResponse>;

/** Roles given together, each at most once. */
const RoleIds = z
  .array(StaffRoleId)
  .min(1)
  .refine(roleIds => new Set(roleIds).size === roleIds.length, {
    message: "Each role can be given once.",
  });

export const AssignStaffRolesRequest = z
  .strictObject({
    userId: UserId.meta({
      description: "The staff member to give the roles to.",
      example: EXAMPLE.userId,
    }),
    roleIds: RoleIds.meta({
      description: "The roles to give them, at least one.",
      example: [EXAMPLE.roleId],
    }),
    pay: MonthlyPay.nullable().optional().meta({
      description:
        "The person's monthly pay from now on, or null to make them unpaid. Omit it to keep their pay; someone unpaid starts at the roles' default pay added together.",
    }),
    nextPayOn: PayDate.optional().meta({
      description:
        "Their next pay date, today or later. Omit it to keep the one they have; paid staff without one are first paid a month from today.",
    }),
  })
  .meta({
    id: "AssignStaffRolesRequest",
    description: "The person, the roles, and optionally their new pay.",
    example: { userId: EXAMPLE.userId, roleIds: [EXAMPLE.roleId], pay: EXAMPLE.pay },
  });
export type AssignStaffRolesRequest = z.infer<typeof AssignStaffRolesRequest>;

export const StaffRoleAssignmentsResponse = z
  .strictObject({
    assignments: z.array(StaffRoleAssignment).meta({
      description: "The new assignments, one for each role given.",
    }),
  })
  .meta({ id: "StaffRoleAssignmentsResponse", description: "The assignments just created." });
export type StaffRoleAssignmentsResponse = z.infer<typeof StaffRoleAssignmentsResponse>;

export const StaffRoleAssignmentResponse = z
  .strictObject({ assignment: StaffRoleAssignment })
  .meta({
    id: "StaffRoleAssignmentResponse",
    description: "The assignment after the change.",
  });
export type StaffRoleAssignmentResponse = z.infer<typeof StaffRoleAssignmentResponse>;

const MemberTitle = z.string().trim().min(1).max(80).nullable().meta({
  description: "The person's staff title, shown to staff managers, or null.",
  example: "Lead moderator",
});

const MemberNotes = z.string().trim().min(1).max(2000).nullable().meta({
  description: "Private notes for staff managers, or null.",
  example: "Covers the EU evening shift.",
});

export const StaffMember = z
  .strictObject({
    user: ProfileSummary.meta({ description: "The staff member." }),
    title: MemberTitle,
    notes: MemberNotes,
    pay: MonthlyPay.nullable().meta({
      description: "The person's monthly pay, or null when they are unpaid.",
    }),
    nextPayOn: PayDate.nullable().meta({
      description: "The next date they are paid, or null when no payment is due.",
    }),
    lastPaidOn: PayDate.nullable().meta({
      description: "The most recent pay date that was paid, or null before their first payment.",
    }),
    activeAssignments: z.array(StaffRoleAssignment).meta({
      description: "The roles the person holds now, newest first.",
    }),
    endedAssignments: z.array(StaffRoleAssignment).meta({
      description: "Roles that were unassigned, newest first: the person's role history.",
    }),
    createdAt: Timestamp.meta({ description: "When the person was added to staff." }),
    updatedAt: Timestamp,
  })
  .meta({
    id: "StaffMember",
    description: "A person on staff, or who was until their last role ended.",
    example: {
      user: EXAMPLE.profile,
      title: "Lead moderator",
      notes: "Covers the EU evening shift.",
      pay: EXAMPLE.pay,
      nextPayOn: EXAMPLE.payDate,
      lastPaidOn: EXAMPLE.lastPaidDate,
      activeAssignments: [ASSIGNMENT_EXAMPLE],
      endedAssignments: [],
      createdAt: EXAMPLE.timestamp,
      updatedAt: EXAMPLE.timestamp,
    },
  });
export type StaffMember = z.infer<typeof StaffMember>;

export const StaffMemberParams = z
  .strictObject({
    userId: UserId.meta({ description: "The staff member's user id.", example: EXAMPLE.userId }),
  })
  .meta({ id: "StaffMemberParams", description: "A route parameter naming one staff member." });
export type StaffMemberParams = z.infer<typeof StaffMemberParams>;

export const AddStaffMemberRequest = z
  .strictObject({
    userId: UserId.meta({ description: "The person to add to staff.", example: EXAMPLE.userId }),
    title: MemberTitle.optional(),
    notes: MemberNotes.optional(),
    roleIds: RoleIds.meta({
      description: "The roles they start with, at least one.",
      example: [EXAMPLE.roleId],
    }),
    pay: MonthlyPay.nullable().meta({
      description: "Their monthly pay, or null when they are unpaid.",
    }),
    nextPayOn: PayDate.optional().meta({
      description:
        "Their first pay date, today or later, for paid staff only. Paid staff without one are first paid a month from today.",
    }),
  })
  .meta({
    id: "AddStaffMemberRequest",
    description: "A new staff member, their first roles, and their pay.",
    example: {
      userId: EXAMPLE.userId,
      title: "Lead moderator",
      roleIds: [EXAMPLE.roleId],
      pay: EXAMPLE.pay,
    },
  });
export type AddStaffMemberRequest = z.infer<typeof AddStaffMemberRequest>;

export const UpdateStaffMemberRequest = z
  .strictObject({
    title: MemberTitle.optional(),
    notes: MemberNotes.optional(),
    pay: MonthlyPay.nullable().optional().meta({
      description: "The person's monthly pay from now on, or null to make them unpaid.",
    }),
    nextPayOn: PayDate.optional().meta({
      description:
        "Move their next pay date. It must be today or later, fall after `lastPaidOn`, and belong to paid staff.",
    }),
  })
  .refine(hasAField, { message: "At least one member field is required." })
  .meta({
    id: "UpdateStaffMemberRequest",
    description: "The fields to change. Null clears a title or notes and makes pay unpaid.",
    example: { pay: EXAMPLE.pay },
  });
export type UpdateStaffMemberRequest = z.infer<typeof UpdateStaffMemberRequest>;

export const StaffMemberResponse = z
  .strictObject({ member: StaffMember })
  .meta({ id: "StaffMemberResponse", description: "One staff member." });
export type StaffMemberResponse = z.infer<typeof StaffMemberResponse>;
